import * as React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { tokens, PasswordInput, EmailInput } from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { SIGN_IN } from '@hotjar/webapp/constants/routes/sso';
import { GoogleSSOButton, SamlSSOButton } from '@hotjar/webapp/components/SSOButton';
import { TextHorizontalRule } from '@hotjar/webapp/components/TextHorizontalRule';
import { ErrorText } from '@hotjar/webapp/components/ErrorText';

import { DemoLoginLoadingState } from '@hotjar/webapp/features/Login/components/DemoLoginLoadingState';
import { useGoogleSsoSignUp } from '@hotjar/webapp/features/Registration/hooks/useGoogleSsoSignUp';
import { REGISTER } from '@hotjar/webapp/constants/routes/index';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_CAMPAIGN_ID,
  SIGNUP_SOURCE,
} from '@hotjar/webapp/features/Registration/common/constants';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { useEffect, useState } from 'react';
import { loginWithEmailAndPassword, redirectLoginOtp, redirectMfaSetup } from '@hotjar/auth';
import { getLoggedIn, refreshFromServer } from '@hotjar/webapp/domains/common/user-data';
import { onTrackLogin } from '@hotjar/webapp/features/Login/track';
import { cropLoginPartFromNextURL, validateNextURL } from '@hotjar/webapp/features/Mfa/getNextURL';
import { getDemoAccountCredentials } from '@hotjar/webapp/features/DemoAccount';
import { useUserLogin } from '@hotjar/auth/src/domains/authentication/useUserLogin';
import { useCurrentSiteId } from '@hotjar/site';
import { navigateToOverview } from '@hotjar/webapp/utils/routing';
import { LoginSuccessState } from './components/LoginSuccessState';
import { LoginLayout } from './components/LoginLayout';

import {
  Title,
  SubTitle,
  Form,
  StyledLink,
  StyledField,
  StyledCheckbox,
  KeepSignedInLabel,
  StyledForgotPasswordLink,
  StyledButton,
  StyledAlert,
} from './styles';

type LoginFormData = {
  email: string;
  password: string;
  remember: boolean;
};

export const Login = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useTrack();
  const [nextUrl, setNextUrl] = useState(useQueryParameter('next'));
  const hasDemoParam = window.location.search === '?demo';
  const {
    register,
    control,
    handleSubmit,
    errors: validationErrors,
    setError,
  } = useForm<LoginFormData>({
    defaultValues: {
      remember: false,
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasNextQueryParam, setHasNextQueryParam] = useState<boolean>(false);
  const [showLoginSuccessState, setShowLoginSuccessState] = useState<boolean>(false);
  const [showDemoLoader, setShowDemoLoader] = useState<boolean>(false);
  const { googleSsoSignUpClick } = useGoogleSsoSignUp();
  const { logoutAndClearData } = useUserLogin();
  const isLoggedIn = getLoggedIn();
  const currentSiteId = useCurrentSiteId();

  useEffect(() => {
    // if url is of type /login?next=%2Flogin we crop that
    // to avoid redirection to login page after successful login
    const updatedNextUrl = cropLoginPartFromNextURL(nextUrl);
    setNextUrl(updatedNextUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (data: LoginFormData) => {
    loginWithEmailAndPassword(data.email, data.password, data.remember)
      // eslint-disable-next-line consistent-return
      .then((response) => {
        if (response.error) {
          return Promise.reject(response.error);
        }
        if (response?.mfaState) {
          redirectLoginOtp(response.mfaState, nextUrl);
        } else if (response?.enforcedMfa) {
          redirectMfaSetup(nextUrl);
        }
      })
      .then(refreshFromServer)
      .then(() => {
        // Track and handle navigation
        onTrackLogin();

        if (nextUrl && (nextUrl.indexOf('download') > -1 || nextUrl.indexOf('/r?') > -1)) {
          if (nextUrl.indexOf('download') > -1) {
            setShowLoginSuccessState(true);
          }

          window.location.href = validateNextURL(nextUrl);
        } else {
          browserHistory.replace(`/${window.location.search}`);
        }
        setIsLoading(false);
      })
      .catch((responseErrors) => {
        track.tagRecording('Login Failed');
        Object.keys(responseErrors).forEach((k) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setError(k, { message: responseErrors[k].toString() });
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (nextUrl) {
      setHasNextQueryParam(true);
    }
  }, [nextUrl, setHasNextQueryParam]);

  useEffect(() => {
    if (isLoggedIn) {
      if (hasDemoParam) {
        logoutAndClearData(undefined, true);
      } else {
        // user is logged in, navigate to overview
        navigateToOverview(currentSiteId);
      }
      return;
    }

    if (hasDemoParam) {
      setShowDemoLoader(true);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      logoutAndClearData(() => {}, true).then(() => {
        const { email, password } = getDemoAccountCredentials();
        handleLogin({ email, password, remember: false });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDemoParam]);

  const redirectToSamlRoute = () => {
    track.click('SSO SignIn Button', 'login', {});
    navigate({ pathname: SIGN_IN.path });
  };
  const getErrorMessage = (key: 'email' | 'password') => validationErrors?.[key]?.message || '';

  const emailError = getErrorMessage('email');
  const passwordError = getErrorMessage('password');
  const onFormSubmit = (data: LoginFormData) => {
    setIsLoading(true);
    handleLogin(data);
  };

  if (showDemoLoader) {
    return <DemoLoginLoadingState />;
  }

  const signUpClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    // We want apply this campaignId for every visitor who clicks
    // the registration link. However, this campaign ID is not set on local/review
    // environments so to prevent from displaying missing campaign id warning we
    // set it based on env.
    const campaignId = getBasedOnEnvironment({
      test: DEFAULT_CAMPAIGN_ID,
      prod: DEFAULT_CAMPAIGN_ID,
      default: null,
    });

    navigate({
      pathname: REGISTER.BASE.path,
      search: `?${campaignId ? `c=${campaignId}&` : ''}signup_source=${SIGNUP_SOURCE.LOGIN_PAGE}`,
    });
  };

  return (
    <LoginLayout className="login-form">
      {showLoginSuccessState ? (
        /* For when login was successful,
               but automatic redirection did not happen because download link was used */
        <LoginSuccessState hasNextQueryParameter={hasNextQueryParam} />
      ) : (
        <Form onSubmit={handleSubmit(onFormSubmit)} data-testid="login-form" noValidate>
          {hasNextQueryParam && (
            <StyledAlert data-testid="alert-info">
              {t('common:login.form.sign-in-to-continue')}
            </StyledAlert>
          )}

          <Title>{t('common:login.form.sign-in')}</Title>
          <SubTitle>
            {t('common:login.form.subtitle')}
            &nbsp;
            <StyledLink
              data-testid="sign-up-link"
              href="#"
              onClick={signUpClickHandler}
              kind="inline"
            >
              {t('common:login.form.sign-up')}
            </StyledLink>
          </SubTitle>
          <GoogleSSOButton
            id="google-button"
            data-testid="google-button"
            onClick={googleSsoSignUpClick}
            containerStyleProps={css`
              margin-bottom: ${tokens.spacer.x3};
            `}
          >
            {t('common:login.form.google-sign-in')}
          </GoogleSSOButton>
          <SamlSSOButton id="saml-button" data-testid="sso-button" onClick={redirectToSamlRoute}>
            {t('common:login.form.saml-sso-sign-in')}
          </SamlSSOButton>

          <TextHorizontalRule message={t('common:login.form.horizontal-text')} />

          <StyledField
            size="l"
            labelText={t('common:login.form.email-field-label')}
            inputId="email"
          >
            <EmailInput
              size="l"
              id="email"
              name="email"
              data-testid="email-input"
              maxLength={100}
              isDisabled={isLoading}
              isInvalid={!!emailError}
              placeholder={t('common:login.form.email-field-placeholder')}
              ref={register({
                required: t('common:login.form.required') as string,
                validate: (value: string) =>
                  validateEmail(value) || (t('common:login.form.invalid-email') as string),
              })}
            />
            {!!emailError && <ErrorText>{emailError}</ErrorText>}
          </StyledField>
          <StyledField
            size="l"
            labelText={t('common:login.form.password-field-label')}
            inputId="password"
          >
            <PasswordInput
              id="password"
              name="password"
              data-testid="password-input"
              size="l"
              isDisabled={isLoading}
              isInvalid={!!passwordError}
              placeholder={t('common:login.form.password-field-placeholder')}
              ref={register({
                required: t('common:login.form.required') as string,
              })}
            />
            {!!passwordError && <ErrorText>{passwordError}</ErrorText>}
            <Controller
              control={control}
              name="remember"
              defaultValue={false}
              render={({ onChange, value }) => (
                <StyledCheckbox
                  id="remember"
                  name="remember"
                  isChecked={value}
                  onChange={() => onChange(!value)}
                  labelContent={
                    <KeepSignedInLabel id="keep-signed-in">
                      {t('common:login.form.keep-signed-in')}
                    </KeepSignedInLabel>
                  }
                />
              )}
            />
          </StyledField>
          <StyledForgotPasswordLink href="/password/forgot" kind="standalone">
            {t('common:login.form.forgot-password')}
          </StyledForgotPasswordLink>
          <StyledButton
            id="submit"
            type="submit"
            isLoading={isLoading}
            data-testid="submit-login"
            kind="primary"
            size="l"
          >
            {t('common:login.form.sign-in')}
          </StyledButton>
        </Form>
      )}
    </LoginLayout>
  );
};
